import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

const FilterContext = createContext();

export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilterContext must be used within a FilterProvider');
  }
  return context;
};

const createDefaultFilters = () => ({
  // Team classification filters
  top25: false,
  rankedMatchups: false,
  fbs: false,
  fcs: false,

  // Conference filters
  conferences: [],
  conferenceType: 'all', // 'all', 'conf', 'non-conf'

  // Betting line filters
  homeUnderdogs: false,
  atsLockAvailable: false,
  overUnderLockAvailable: false,
  spreadMin: null,
  spreadMax: null,
  overUnderMin: null,
  overUnderMax: null,

  // Game type filters
  neutralSite: false,

  // Game status filters
  hideCompleted: false,
  hideInProgress: false,
  hideNotStarted: false,

  // Team search filter
  teamSearch: '',

  // Advanced filters
  excitementIndex: null,
  eloDifference: null,
  excitementIndexMin: null,
  excitementIndexMax: null,
  eloDifferenceMin: null,
  eloDifferenceMax: null,

  // Smart filters
  closeMatchups: false,
  mismatches: false,
  barnBurners: false,

  // Filter logic
  filterLogic: 'AND' // 'AND' or 'OR'
});

export const FilterProvider = ({ children }) => {
  // Filter state
  const [filters, setFilters] = useState(createDefaultFilters);

  // Quick preset filters
  const [activePreset, setActivePreset] = useState(null);

  // Flag to prevent saving during initial load
  const [isInitialized, setIsInitialized] = useState(false);

  // Load filters from localStorage on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem('filters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters(prev => ({
          ...prev,
          ...parsed
        }));
      } catch (error) {
        console.error('Error loading filters from localStorage:', error);
      }
    }

    const savedPreset = localStorage.getItem('activePreset');
    if (savedPreset) {
      try {
        const parsed = JSON.parse(savedPreset);
        setActivePreset(parsed);
      } catch (error) {
        console.error('Error loading active preset from localStorage:', error);
      }
    }
    setIsInitialized(true);
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem('filters', JSON.stringify(filters));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
    }
  }, [filters, isInitialized]);

  // Save active preset to localStorage whenever it changes
  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem('activePreset', JSON.stringify(activePreset));
      } catch (error) {
        console.error('Error saving active preset to localStorage:', error);
      }
    }
  }, [activePreset, isInitialized]);

  // Update a single filter
  const updateFilter = useCallback((filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  }, []);

  // Update multiple filters at once
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  // Apply a preset filter
  const applyPreset = useCallback((presetName) => {
    const presets = {
      'small-spread': {
        spreadMax: 3,
        spreadMin: null,
        top25: false,
        rankedMatchups: false,
        fbs: false,
        fcs: false,
        conferences: [],
        homeUnderdogs: false,
        overUnderMin: null,
        overUnderMax: null,
        neutralSite: false
      },
      'large-spread': {
        spreadMin: 15,
        spreadMax: null,
        top25: false,
        rankedMatchups: false,
        fbs: false,
        fcs: false,
        conferences: [],
        homeUnderdogs: false,
        overUnderMin: null,
        overUnderMax: null,
        neutralSite: false
      },
      'small-total': {
        overUnderMax: 35,
        overUnderMin: null,
        top25: false,
        rankedMatchups: false,
        fbs: false,
        fcs: false,
        conferences: [],
        homeUnderdogs: false,
        spreadMin: null,
        spreadMax: null,
        neutralSite: false
      },
      'large-total': {
        overUnderMin: 70,
        overUnderMax: null,
        top25: false,
        rankedMatchups: false,
        fbs: false,
        fcs: false,
        conferences: [],
        homeUnderdogs: false,
        spreadMin: null,
        spreadMax: null,
        neutralSite: false
      },
      'top25-only': {
        top25: true,
        rankedMatchups: false,
        fbs: false,
        fcs: false,
        conferences: [],
        homeUnderdogs: false,
        spreadMin: null,
        spreadMax: null,
        overUnderMin: null,
        overUnderMax: null,
        neutralSite: false
      },
      'ranked-matchups': {
        rankedMatchups: true,
        top25: false,
        fbs: false,
        fcs: false,
        conferences: [],
        homeUnderdogs: false,
        spreadMin: null,
        spreadMax: null,
        overUnderMin: null,
        overUnderMax: null,
        neutralSite: false
      },
      'fbs-only': {
        fbs: true,
        fcs: false,
        top25: false,
        rankedMatchups: false,
        conferences: [],
        homeUnderdogs: false,
        spreadMin: null,
        spreadMax: null,
        overUnderMin: null,
        overUnderMax: null,
        neutralSite: false
      },
      'home-underdogs': {
        homeUnderdogs: true,
        top25: false,
        rankedMatchups: false,
        fbs: false,
        fcs: false,
        conferences: [],
        spreadMin: null,
        spreadMax: null,
        overUnderMin: null,
        overUnderMax: null,
        neutralSite: false
      },
      'neutral-site': {
        neutralSite: true,
        top25: false,
        rankedMatchups: false,
        fbs: false,
        fcs: false,
        conferences: [],
        homeUnderdogs: false,
        spreadMin: null,
        spreadMax: null,
        overUnderMin: null,
        overUnderMax: null
      }
    };

    if (presets[presetName]) {
      setFilters(prev => ({
        ...prev,
        ...presets[presetName]
      }));
      setActivePreset(presetName);
    }
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(createDefaultFilters());
    setActivePreset(null);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.top25 ||
      filters.rankedMatchups ||
      filters.fbs ||
      filters.fcs ||
      filters.conferences.length > 0 ||
      filters.conferenceType !== 'all' ||
      filters.homeUnderdogs ||
      filters.atsLockAvailable ||
      filters.overUnderLockAvailable ||
      filters.spreadMin !== null ||
      filters.spreadMax !== null ||
      filters.overUnderMin !== null ||
      filters.overUnderMax !== null ||
      filters.neutralSite ||
      filters.hideCompleted ||
      filters.hideInProgress ||
      filters.hideNotStarted ||
      filters.teamSearch.trim() !== '' ||
      filters.excitementIndex !== null ||
      filters.eloDifference !== null ||
      filters.excitementIndexMin !== null ||
      filters.excitementIndexMax !== null ||
      filters.eloDifferenceMin !== null ||
      filters.eloDifferenceMax !== null ||
      filters.closeMatchups ||
      filters.mismatches ||
      filters.barnBurners
    );
  }, [filters]);

  // Get available conferences from games
  const getAvailableConferences = useCallback((games) => {
    const conferences = new Set();
    games.forEach(game => {
      if (game.home_team?.conference) {
        conferences.add(game.home_team.conference);
      }
      if (game.away_team?.conference) {
        conferences.add(game.away_team.conference);
      }
    });
    return Array.from(conferences).sort((a, b) => a.localeCompare(b));
  }, []);

  const value = useMemo(() => ({
    filters,
    activePreset,
    updateFilter,
    updateFilters,
    applyPreset,
    clearFilters,
    hasActiveFilters,
    getAvailableConferences
  }), [filters, activePreset, updateFilter, updateFilters, applyPreset, clearFilters, hasActiveFilters, getAvailableConferences]);

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};

FilterProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default FilterContext;
